import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import "./EventDetailsPage.css";

const EventDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [bringVests, setBringVests] = useState(false);
    const [guestName, setGuestName] = useState("");
    const [guestRole, setGuestRole] = useState("külaline");
    const [addingGuest, setAddingGuest] = useState(false);

    const decodeJWT = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.codePointAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error decoding JWT:', error);
            return null;
        }
    };

    const getCostForRole = (event) => {
        if (!userRole || !event) {
            return event?.cost || null;
        }

        const role = userRole.toLowerCase();
        switch (role) {
            case "ksv!":
                return event.costKsv !== null && event.costKsv !== undefined ? event.costKsv : event.cost;
            case "külaline":
                return event.costKulaline !== null && event.costKulaline !== undefined ? event.costKulaline : event.cost;
            case "vil!":
                return event.costVil !== null && event.costVil !== undefined ? event.costVil : event.cost;
            case "noorliige":
                return event.costEiorliige !== null && event.costEiorliige !== undefined ? event.costEiorliige : event.cost;
            default:
                return event.cost;
        }
    };

    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (token) {
            const decoded = decodeJWT(token);
            if (decoded?.role) {
                setUserRole(decoded.role);
            }
        }
    }, []);

    useEffect(() => {
        if (!id) {
            setLoading(false);
            return;
        }
        axiosInstance
            .get(`/api/events/${id}`)
            .then((response) => {
                setEvent(response.data);
                // Set bringVests checkbox based on whether current user is bringing vests
                const userId = localStorage.getItem('userId');
                if (userId && response.data.vestBringerUserId) {
                    setBringVests(String(response.data.vestBringerUserId) === String(userId));
                } else {
                    setBringVests(false);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching event:", err);
                setError(err.response?.data?.message || err.message || "Ürituse laadimine ebaõnnestus");
                setLoading(false);
            });
    }, [id]);

    const handleJoinEvent = () => {
        const userId = localStorage.getItem('userId');

        if (!userId) {
            alert("Üritusega liitumiseks logi sisse");
            return;
        }

        // Try to join the event (backend will automatically add to waitlist if full)
        axiosInstance.post(`/api/events/joinEvent/${userId}/${id}`, { bringVests: bringVests })
            .then((response) => {
                // Check if user was added to waitlist or joined the event
                const eventData = response.data;
                const userIdStr = String(userId);
                const isInUsers = Array.isArray(eventData.users) && eventData.users.some((u) => String(u.id) === userIdStr);
                const isInWaitlist = Array.isArray(eventData.waitlist) && eventData.waitlist.some((u) => String(u.id) === userIdStr);

                if (isInUsers) {
                    alert("Liitusid üritusega!");
                } else if (isInWaitlist) {
                    alert("Üritus on täis. Sind on lisatud waitlisti!");
                } else {
                    alert("Liitusid üritusega!");
                }

                // Reset checkbox
                setBringVests(false);

                // Refresh event data
                axiosInstance.get(`/api/events/${id}`)
                    .then((response) => {
                        setEvent(response.data);
                    })
                    .catch((err) => {
                        console.error("Viga ürituse värskendamisel:", err);
                    });
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || error.response?.data?.error || "Error joining event";
                alert(errorMessage);
                // Refresh event data even on error
                axiosInstance.get(`/api/events/${id}`)
                    .then((response) => {
                        setEvent(response.data);
                    })
                    .catch((err) => {
                        console.error("Viga ürituse värskendamisel:", err);
                    });
            });
    };

    const handleLeaveEvent = () =>
    {
        const userId = localStorage.getItem('userId');
        if (!userId) {
            alert("Palun logi sisse");
            return;
        }

        axiosInstance.post(`/api/events/leaveEvent/${userId}/${id}`).then((response) => {
            setEvent(response.data);
        })
            .catch((err) => {
                console.error("Viga ürituse värskendamisel:", err);
            });
    }

    const handleUpdateVestBringer = () => {
        const userId = localStorage.getItem('userId');

        if (!userId) {
            alert("Palun logi sisse");
            return;
        }

        axiosInstance.put(`/api/events/updateVestBringer/${userId}/${id}`, { bringVests: bringVests })
            .then(() => {
                if (bringVests) {
                    alert("Võtad nüüd vestid kaasa!");
                } else {
                    alert("Ei võta enam veste kaasa.");
                }

                axiosInstance.get(`/api/events/${id}`)
                    .then((response) => {
                        setEvent(response.data);
                        setBringVests(response.data.vestBringerUserId === userId);
                    })
                    .catch((err) => {
                        console.error("Viga ürituse värskendamisel:", err);
                    });
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || error.response?.data?.error || "Error updating vest bringer";
                alert(errorMessage);
                axiosInstance.get(`/api/events/${id}`)
                    .then((response) => {
                        setEvent(response.data);
                        setBringVests(response.data.vestBringerUserId === userId);
                    })
                    .catch((err) => {
                        console.error("Viga ürituse värskendamisel:", err);
                    });
            });
    };

    const isLoggedIn = () => {
        return !!localStorage.getItem("authToken");
    };

    // Backend only sends initials (no names) to visitors who are not logged in
    const displayUserName = (user) => {
        if (isLoggedIn() && user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.initials || "?";
    };

    // Guest names are free text, so initials for visitors are built here
    const displayGuestName = (guest) => {
        if (isLoggedIn()) {
            return guest.name;
        }
        const parts = (guest.name || "").trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return "?";
        const first = parts[0][0].toUpperCase();
        if (parts.length === 1) return `${first}.`;
        return `${first}. ${parts[parts.length - 1][0].toUpperCase()}.`;
    };

    const parseEventDate = (dateString) => {
        if (!dateString) return null;
        const [datePart, timePart] = dateString.split(' ');
        if (!datePart || !timePart) return null;
        const [day, month, year] = datePart.split('.').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
        const date = new Date(year, month - 1, day, hour, minute);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const isPastEvent = () => {
        const endTime = parseEventDate(event?.endTime);
        return endTime ? endTime < new Date() : false;
    };

    const isUserJoined = () => {
        if (!event || !isLoggedIn()) return false;
        const userId = localStorage.getItem("userId");
        if (!userId) return false;
        return event.users?.some(user =>
            String(user.id) === String(userId)
        ) ?? false;
    };

    const handleAddGuest = () => {
        const userId = localStorage.getItem("userId");
        const name = guestName.trim();
        if (!userId || !name) return;
        setAddingGuest(true);
        axiosInstance
            .post(`/api/events/addGuest/${userId}/${id}`, { name, role: guestRole || "külaline" })
            .then((response) => {
                setEvent(response.data);
                setGuestName("");
                const added = response.data.guests?.find((g) => g.name === name);
                if (added?.onWaitlist) {
                    alert("Üritus on täis. Lisatud waitlisti");
                }
            })
            .catch((err) => {
                alert(err.response?.data?.message || err.message || "Failed to add guest");
            })
            .finally(() => setAddingGuest(false));
    };

    const isUserOnWaitlist = () => {
        if (!event || !isLoggedIn()) return false;
        const userId = localStorage.getItem('userId');
        if (!userId) return false;
        return Array.isArray(event.waitlist) && event.waitlist.some((u) => {
            if (u.id !== undefined && u.id !== null) {
                return String(u.id) === String(userId);
            }
            return false;
        });
    };

    if (loading) return <div className="event-details-container">Laadimine...</div>;
    if (error) return <div className="event-details-container">Viga: {error}</div>;
    if (!event) return <div className="event-details-container">Üritust ei leitud</div>;

    return (
        <div className="event-details-container">
            <button onClick={() => navigate("/events")} className="back-button">
                ← Tagasi ürituste juurde
            </button>
            <div className="event-details">
                <h1>{event.name || event.title}</h1>
                <div className="event-info">
                    <div className="info-row">
                        <span className="info-label">Algus:</span>
                        <span className="info-value">{event.startTime}</span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">Lõpp:</span>
                        <span className="info-value">{event.endTime}</span>
                    </div>
                    {event.location && (
                        <div className="info-row">
                            <span className="info-label">Asukoht:</span>
                            <span className="info-value">{event.location}</span>
                        </div>
                    )}
                    <div className="info-row">
                        <span className="info-label">Osalustasu:</span>
                        <span className="info-value">
                            {(() => {
                                if (!isLoggedIn()) {
                                    return 'Hinna nägemiseks logi sisse';
                                }
                                const cost = getCostForRole(event);
                                if (cost !== null && cost !== undefined && cost > 0) {
                                    return `€${cost.toFixed(2)}`;
                                }
                                // Only show "Tasuta" if role is "noorliige"
                                if (userRole?.toLowerCase() === 'noorliige') {
                                    return 'Tasuta';
                                }
                                // For other roles, show €0.00 if cost is 0
                                return '€0.00';
                            })()}
                        </span>
                    </div>
                    {event.participantLimit && (
                        <div className="info-row">
                            <span className="info-label">Kohtade arv:</span>
                            <span className="info-value">
                                {event.participantLimit} {event.users?.length >= event.participantLimit ? '(Full)' : ''}
                            </span>
                        </div>
                    )}
                </div>

                <div className="event-users-section">
                    <h2>
                        Osalejad ({((event.users?.length || 0) + (event.guests?.filter((g) => !g.onWaitlist).length || 0))}
                        {event.participantLimit ? ` / ${event.participantLimit}` : ''})
                    </h2>
                    {(Array.isArray(event.users) && event.users.length > 0) || (Array.isArray(event.guests) && event.guests.some((g) => !g.onWaitlist)) ? (
                        <div className="users-list">
                            {Array.isArray(event.users) && event.users.map((user, index) => (
                                <div key={"u-" + (user.id || index)} className="user-card">
                                    <div className="user-name">
                                        {displayUserName(user)}
                                    </div>
                                    {isLoggedIn() && <div className="user-role">{user.role || '—'}</div>}
                                </div>
                            ))}
                            {Array.isArray(event.guests) && event.guests.filter((g) => !g.onWaitlist).map((g, index) => (
                                <div key={"g-" + (g.id || index)} className="user-card">
                                    <div className="user-name">{displayGuestName(g)}</div>
                                    {isLoggedIn() && <div className="user-role">{g.role || '—'}</div>}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-users">Osalejad puuduvad</div>
                    )}
                </div>

                {(() => {
                    const waitlistUsers = Array.isArray(event.waitlist) ? event.waitlist : [];
                    const waitlistGuests = Array.isArray(event.guests) ? event.guests.filter((g) => g.onWaitlist) : [];
                    const waitlistTotal = waitlistUsers.length + waitlistGuests.length;
                    if (waitlistTotal === 0) return null;
                    return (
                        <div className="event-users-section">
                            <h2>Ootenimekiri ({waitlistTotal})</h2>
                            <div className="users-list">
                                {waitlistUsers.map((user, index) => (
                                    <div key={"w-u-" + (user.id || index)} className="user-card">
                                        <div className="user-name">{displayUserName(user)}</div>
                                        {isLoggedIn() && user.role && <div className="user-email">{user.role}</div>}
                                    </div>
                                ))}
                                {waitlistGuests.map((g, index) => (
                                    <div key={"w-g-" + (g.id || index)} className="user-card">
                                        <div className="user-name">{displayGuestName(g)}</div>
                                        {isLoggedIn() && <div className="user-role">{g.role || '—'}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {event.vestBringerUserId && (() => {
                    const vestBringer = event.users?.find(u => String(u.id) === String(event.vestBringerUserId));
                    const userName = vestBringer ? displayUserName(vestBringer) : 'Keegi';
                    return (
                        <div className="vest-info-text">
                            <span>{userName} võtab kaasa vestid.</span>
                        </div>
                    );
                })()}
                {isLoggedIn() && (
                    (!event.vestBringerUserId || localStorage.getItem('userId') === String(event.vestBringerUserId)) && (
                <div className="vest-checkbox-container">
                    <label className="vest-checkbox-label">
                        <input
                            type="checkbox"
                            checked={bringVests}
                            onChange={(e) => setBringVests(e.target.checked)}
                            className="vest-checkbox"
                        />
                        <span>Võtan kaasa vestid</span>
                    </label>
                    <button className="vest-save-button" onClick={handleUpdateVestBringer}>
                        Salvesta muudatused
                    </button>
                </div>
            ))}
                {isLoggedIn() && !isPastEvent() && !isUserJoined() && (
                    <div className="join-section">
                        <button onClick={handleJoinEvent} className="join-button">
                            {event.participantLimit && event.users?.length >= event.participantLimit
                                ? "Liitu ootenimekirjaga"
                                : "Liitu sündmusega"}
                        </button>
                    </div>

                )}

                {(isUserJoined() || isUserOnWaitlist()) && isLoggedIn() && !isPastEvent() && (
                    <div className="join-section">
                        {isUserOnWaitlist() ? (
                            <button onClick={handleLeaveEvent}>
                                Lahku waitlistist
                            </button>
                            ) : (
                            <button onClick={handleLeaveEvent}>
                                Lahku üritusest
                            </button>
                        )}
                        </div>
                )}
                {isLoggedIn() && !isPastEvent() && (() => {
                    return (
                        <div className="join-section guest-controls">
                            <input className="guest-name-input" type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Külalise nimi (Ära pane enda nime siia)" />
                            <select className="guest-role-select" value={guestRole} onChange={(e) => setGuestRole(e.target.value)}>
                                <option value="ksv!">ksv!</option>
                                <option value="vil!">vil!</option>
                                <option value="külaline">külaline</option>
                                <option value="noorliige">noorliige</option>
                            </select>
                            <button type="button" onClick={handleAddGuest} disabled={addingGuest || !guestName.trim()} className="add-guest-btn" title="Add">+</button>
                        </div>
                    );
                })()}
                {isLoggedIn() && isUserOnWaitlist() && !isUserJoined() && (
                    <div className="joined-badge">Oled ootenimekirjas</div>
                )}
                {!isLoggedIn() && (
                    <div className="login-prompt">
                        Palun logi sisse, et liituda sündmusega
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventDetailsPage;
